import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { Stack, Tabs } from '@mantine/core';
import { IconCoin, IconFileImport, IconMapPin, IconPackage, IconPlugConnected, IconSettings, IconUsers } from '@tabler/icons-react';

/** Owned by G26 (plan.md S0). Layout route for the Settings screen group:
 * tenant config, sites/locations, cost centres, products (+pack specs),
 * users/roles, Xero connection + mappings, and the Unleashed importer — NOT
 * supplier contracts (G20), stages/recipes (G21) or silo calibration (G22),
 * which live with their owning modules (BUILD_SPEC.md §11, plan.md's QC fixes
 * #1-3). Sub-navigation mirrors the Shell's own NavLink pattern
 * (src/components/Shell.tsx) at a smaller scale, and features/silo-counts's
 * own layout route (silo-counts.tsx) for the Tabs-over-Outlet shape. */
export const Route = createFileRoute('/_authenticated/settings')({
  component: SettingsLayout,
});

// Exported so nav.test.ts can assert UTILITY_ITEMS' "Settings" children mirror
// this verbatim (label + to + order) — same convention as sales-invoicing.tsx's
// own exported TABS (see nav.ts's module comment on children mirroring).
export const TABS = [
  { to: '/settings', label: 'Tenant', icon: IconSettings, match: (path: string) => path === '/settings' },
  {
    to: '/settings/sites-locations',
    label: 'Sites & locations',
    icon: IconMapPin,
    match: (path: string) => path.startsWith('/settings/sites-locations'),
  },
  {
    to: '/settings/cost-centres',
    label: 'Cost centres',
    icon: IconCoin,
    match: (path: string) => path.startsWith('/settings/cost-centres'),
  },
  {
    to: '/settings/products',
    label: 'Products',
    icon: IconPackage,
    match: (path: string) => path.startsWith('/settings/products'),
  },
  { to: '/settings/users', label: 'Users & roles', icon: IconUsers, match: (path: string) => path.startsWith('/settings/users') },
  {
    to: '/settings/xero',
    label: 'Xero',
    icon: IconPlugConnected,
    // Also matches the OAuth callback relay (/settings/xero/callback), so the
    // tab bar stays on "Xero" while the connect round-trip completes.
    match: (path: string) => path.startsWith('/settings/xero'),
  },
  {
    to: '/settings/importer',
    label: 'Importer',
    icon: IconFileImport,
    match: (path: string) => path.startsWith('/settings/importer'),
  },
] as const;

function SettingsLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const active = TABS.find((t) => t.match(pathname))?.to ?? '/settings';

  return (
    <Stack gap="lg">
      <Tabs value={active}>
        <Tabs.List>
          {TABS.map((tab) => (
            <Tabs.Tab
              key={tab.to}
              value={tab.to}
              leftSection={<tab.icon size={16} />}
              // See silo-counts.tsx's identical comment: `Tabs.Tab`'s `component`
              // prop is typed `any`, so `renderRoot` is Mantine's supported escape
              // hatch for rendering the tab onto an arbitrary root element (here,
              // a TanStack Router `Link`).
              renderRoot={(props) => <Link to={tab.to} {...props} />}
            >
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>
      <Outlet />
    </Stack>
  );
}
